/**
 * Trigger Release Dialog
 *
 * Dialog for starting a new Release QA run
 */

import React from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import {
  DialogTrigger,
  Dialog,
  DialogContent,
  DialogFooter,
  Button,
  Select,
  Text,
  Flex,
  Box,
  useToast,
} from '@chatsdet/ui';
import { Warning, Gear } from '@phosphor-icons/react';
import { useStartReleaseRun, useReleaseConfig } from '../useReleases';
import { useOrgPropertySelection } from '../../../hooks/useOrgPropertySelection';
import { toastErrorOptionsFor } from '../../../lib/toast-error-options';

type TriggerReleaseDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-selected property ID (optional) */
  defaultPropertyId?: string;
};

export const TriggerReleaseDialog: React.FC<TriggerReleaseDialogProps> = ({
  isOpen,
  onOpenChange,
  defaultPropertyId,
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const startMutation = useStartReleaseRun();

  const [selectedPropertyId, setSelectedPropertyId] = React.useState<string | null>(
    defaultPropertyId ?? null
  );

  const { propertyOptions } = useOrgPropertySelection();

  // Fetch config for selected property to validate it's configured
  const configQuery = useReleaseConfig(selectedPropertyId ?? undefined);
  const hasConfig = configQuery.data?.config !== null && configQuery.data?.config !== undefined;
  const isConfigLoading = configQuery.isLoading && !!selectedPropertyId;

  // Reset form when dialog opens
  React.useEffect(() => {
    if (isOpen) {
      setSelectedPropertyId(defaultPropertyId ?? null);
    }
  }, [isOpen, defaultPropertyId]);

  const handleSubmit = async () => {
    if (!selectedPropertyId) {
      toast.info('Please select an application');
      return;
    }

    try {
      const result = await startMutation.mutateAsync({
        propertyId: selectedPropertyId,
      });

      toast.success('Release started');
      onOpenChange(false);

      // Navigate to the timeline with the new run preselected in the
      // master-detail pane (see `ReleasePropertyTimeline`).
      navigate({
        to: '/releases/$propertyId',
        params: { propertyId: selectedPropertyId },
        search: { runId: result.run.releaseRunId },
      });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to start release run',
        toastErrorOptionsFor(error)
      );
    }
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange} size="md">
      <span style={{ display: 'none' }} />
      <Dialog
        title="Run release"
        description="Analyze code changes and run targeted QA tests"
      >
        <DialogContent>
          <div className="space-y-4">
            <Select
              label="Application"
              placeholder="Select an application"
              items={propertyOptions.map((opt) => ({ id: opt.id, label: opt.label }))}
              selectedKey={selectedPropertyId}
              onSelectionChange={(key) => setSelectedPropertyId(key as string)}
            />

            {/* Show warning if property is not configured */}
            {selectedPropertyId && !isConfigLoading && !hasConfig && (
              <Box className="p-4 bg-warning/10 border border-warning/30 rounded-lg">
                <Flex align="start" gap="sm">
                  <Warning size={20} className="text-warning shrink-0 mt-0.5" />
                  <Box>
                    <Text weight="medium">Release QA not configured</Text>
                    <Text size="sm" className="text-muted-foreground mt-1">
                      This application needs to be configured for Release QA before you can start a
                      run. You'll need to link a GitHub repository and set up the configuration.
                    </Text>
                    <Link
                      to="/applications/$propertyId"
                      params={{ propertyId: selectedPropertyId }}
                      search={{ tab: 'releases' }}
                      className="inline-flex items-center gap-1.5 mt-2 text-sm text-primary hover:underline"
                      onClick={() => onOpenChange(false)}
                    >
                      <Gear size={14} />
                      Configure Release QA
                    </Link>
                  </Box>
                </Flex>
              </Box>
            )}
          </div>
        </DialogContent>
        <DialogFooter>
          <Button
            variant="outline"
            onPress={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onPress={() => void handleSubmit()}
            isDisabled={
              !selectedPropertyId || !hasConfig || isConfigLoading || startMutation.isPending
            }
          >
            {startMutation.isPending ? 'Starting...' : 'Run'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
};
